import React, { useImperativeHandle, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { Box, Card, Chip, DateField, FormHelperText, InputAdornment, MenuItem, RadioField, SelectField, TextField, Tooltip, Typography } from '@curves/ui-base';
import { ReadTextField } from '@curves/ui-shared';
import { CAMPAIGN_DISCOUNT_METHOD } from '@Constants';
import { parseDate } from '@Util/moment.util';
import { ensureArray, matchOptionsText } from '@Util/utils';
import { DISCOUNT_TYPE_TIP, formatDiscountText, getDiscountValueError } from './CampaignEdit.config';

// 欄位格線間距：列距大於欄距，讓上下欄位不黏在一起
const GRID_SPACING = { rowSpacing: 3, columnSpacing: 2 };

/**
 * 卡片標題列
 * - 左側標題，右側可放狀態標籤或操作
 */
const CardHeader = ({ title, children }) => (
    <Box className="campaign-edit-card__header">
        <Typography variant="h6" className="campaign-edit-card__title">
            {title}
        </Typography>
        {children}
    </Box>
);

/**
 * 卡片標題旁的鎖定標籤
 * - 滑過顯示不可修改的原因
 */
const LockChip = ({ label, reason }) => (
    <Tooltip title={reason}>
        <span>
            <Chip size="small" color="gray" label={label} />
        </span>
    </Tooltip>
);

// 唯讀欄位下方的後端錯誤訊息，沒有錯誤時不顯示
const ReadOnlyError = ({ message }) => (message ? <FormHelperText isError>{message}</FormHelperText> : null);

// 讀回的折扣方式可能含商品限制旗標（9、10），表單只保留現金或百分比
const toFormDiscountMethod = discountMethod =>
    String(discountMethod & CAMPAIGN_DISCOUNT_METHOD.PERCENT ? CAMPAIGN_DISCOUNT_METHOD.PERCENT : CAMPAIGN_DISCOUNT_METHOD.CASH);

/**
 * 抵用券基本資料與折扣設定，分成兩張卡片，統一兩欄格線
 * - 欄位不受控，由 ref 提供 isError、getResult；只有會改變畫面的折扣方式用 state
 * - 後端欄位錯誤（validationErrors）顯示在對應欄位
 * - 已被訂單使用時只有使用期間可編輯，其餘改唯讀並以讀回值送出，卡片標題旁顯示鎖定標籤
 */
const CampaignBasicForm = React.forwardRef(({ options, campaign, isUsed, validationErrors = {} }, ref) => {
    // 讀回的折扣方式（只留現金或百分比），新增時為現金
    const initialDiscountMethod = campaign ? toFormDiscountMethod(campaign.discountMethod) : String(CAMPAIGN_DISCOUNT_METHOD.CASH);
    // 折扣方式，切換時要改折抵數值的名稱、提示與單位
    const [discountMethod, setDiscountMethod] = useState(initialDiscountMethod);
    // 各欄位的 ref，送出時檢核與取值
    const campaignNameRef = useRef(null);
    const sourceTypeRef = useRef(null);
    const discountValueRef = useRef(null);
    const discountTypeRef = useRef(null);
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // 目前是否為百分比折扣，決定折抵數值的名稱、提示與單位
    const isPercent = Number(discountMethod) === CAMPAIGN_DISCOUNT_METHOD.PERCENT;
    // 折抵數值欄位名稱，依折扣方式顯示單位
    const discountValueLabel = isPercent ? '折抵數值（折數）' : '折抵數值（元）';
    // 折抵數值預設值：折扣方式與讀回相同時帶讀回值，切換後清空
    const defaultDiscountValue = campaign && discountMethod === initialDiscountMethod ? String(campaign.discountValue) : '';

    useImperativeHandle(ref, () => ({
        // 檢核所有欄位並標示錯誤，有錯回傳 true；已使用時只檢核使用期間
        isError: () => {
            const dateErrors = [startDateRef.current.isError(), endDateRef.current.isError()];
            if (isUsed) return dateErrors.some(Boolean);
            const discountValueError = getDiscountValueError(discountMethod, discountValueRef.current.getResult());
            discountValueRef.current.setError(!!discountValueError, discountValueError);
            return [...dateErrors, campaignNameRef.current.isError(), sourceTypeRef.current.isError(), discountTypeRef.current.isError(), !!discountValueError].some(Boolean);
        },
        // 取得欄位值；已使用時唯讀欄位以讀回值送出
        getResult: () => ({
            campaignName: isUsed ? campaign.campaignName : campaignNameRef.current.getResult(),
            sourceType: isUsed ? campaign.sourceType : sourceTypeRef.current.getResult(),
            discountMethod,
            discountType: isUsed ? campaign.discountType : discountTypeRef.current.getResult(),
            discountValue: isUsed ? campaign.discountValue : discountValueRef.current.getResult(),
            startDate: startDateRef.current.getResult(),
            endDate: endDateRef.current.getResult(),
        }),
    }));

    return (
        <>
            <Card className="campaign-edit-card">
                <CardHeader title="基本資料">{isUsed && <LockChip label="部分不可修改" reason="此抵用券已被訂單使用，僅可修改使用期間" />}</CardHeader>
                <Grid container {...GRID_SPACING}>
                    <Grid xs={12} md={6} item>
                        {isUsed ? (
                            <ReadTextField label="名稱">{campaign.campaignName}</ReadTextField>
                        ) : (
                            <TextField
                                ref={campaignNameRef}
                                label="名稱"
                                defaultValue={campaign?.campaignName ?? ''}
                                maxLength={50}
                                isError={!!validationErrors.campaignName}
                                helperText={validationErrors.campaignName || '請輸入名稱'}
                                InputProps={{ placeholder: '請輸入抵用券名稱' }}
                                fullWidth
                                required
                            />
                        )}
                    </Grid>
                    <Grid xs={12} md={6} item>
                        {isUsed ? (
                            <>
                                <ReadTextField label="發行來源">{matchOptionsText(ensureArray(options?.sourceTypes), campaign.sourceType, '－')}</ReadTextField>
                                <ReadOnlyError message={validationErrors.sourceType} />
                            </>
                        ) : (
                            <SelectField ref={sourceTypeRef} label="發行來源" defaultValue={campaign ? String(campaign.sourceType) : ''} isError={!!validationErrors.sourceType} helperText={validationErrors.sourceType || '請選擇發行來源'} fullWidth required>
                                {ensureArray(options?.sourceTypes).map(({ value, text }) => (
                                    <MenuItem key={value} value={String(value)}>
                                        {text}
                                    </MenuItem>
                                ))}
                            </SelectField>
                        )}
                    </Grid>
                    <Grid xs={12} md={6} item>
                        <DateField ref={startDateRef} defaultValue={campaign ? parseDate(campaign.startDate) : ''} label="使用開始日" fullWidth required />
                    </Grid>
                    <Grid xs={12} md={6} item>
                        <DateField ref={endDateRef} defaultValue={campaign ? parseDate(campaign.endDate) : ''} label="使用結束日" fullWidth required />
                    </Grid>
                </Grid>
            </Card>
            <Card className="campaign-edit-card">
                <CardHeader title="折扣設定">{isUsed && <LockChip label="不可修改" reason="此抵用券已被訂單使用，折扣設定不可修改" />}</CardHeader>
                {isUsed ? (
                    <Grid container {...GRID_SPACING}>
                        <Grid xs={12} md={4} item>
                            <ReadTextField label="折扣方式">{matchOptionsText(ensureArray(options?.discountMethods), discountMethod, '－')}</ReadTextField>
                            <ReadOnlyError message={validationErrors.discountMethod} />
                        </Grid>
                        <Grid xs={12} md={4} item>
                            <ReadTextField label="折抵數值">{formatDiscountText({ discountMethod: Number(discountMethod), discountValue: Number(campaign.discountValue) })}</ReadTextField>
                        </Grid>
                        <Grid xs={12} md={4} item>
                            <ReadTextField label="基準價" tip={DISCOUNT_TYPE_TIP}>
                                {matchOptionsText(ensureArray(options?.discountTypes), campaign.discountType, '－')}
                            </ReadTextField>
                            <ReadOnlyError message={validationErrors.discountType} />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container {...GRID_SPACING}>
                        <Grid xs={12} item>
                            <RadioField label="折扣方式" value={discountMethod} onChange={(_, value) => setDiscountMethod(value)} isError={!!validationErrors.discountMethod} helperText={validationErrors.discountMethod} row required fullWidth>
                                {ensureArray(options?.discountMethods).map(({ value, text }) => (
                                    <RadioField.Radio key={value} value={value} label={text} />
                                ))}
                            </RadioField>
                        </Grid>
                        <Grid xs={12} md={6} item>
                            <TextField
                                key={discountMethod}
                                ref={discountValueRef}
                                label={discountValueLabel}
                                defaultValue={defaultDiscountValue}
                                InputProps={{
                                    placeholder: isPercent ? '例：0.85 代表 85 折' : '例：100',
                                    endAdornment: isPercent ? null : <InputAdornment position="end">元</InputAdornment>,
                                }}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid xs={12} md={6} item>
                            <SelectField
                                ref={discountTypeRef}
                                label="基準價"
                                FormLabelTip={DISCOUNT_TYPE_TIP}
                                defaultValue={campaign ? String(campaign.discountType) : ''}
                                isError={!!validationErrors.discountType}
                                helperText={validationErrors.discountType || '請選擇基準價'}
                                fullWidth
                                required
                            >
                                {ensureArray(options?.discountTypes).map(({ value, text }) => (
                                    <MenuItem key={value} value={String(value)}>
                                        {text}
                                    </MenuItem>
                                ))}
                            </SelectField>
                        </Grid>
                    </Grid>
                )}
            </Card>
        </>
    );
});

export default CampaignBasicForm;
